/* eslint-disable max-nested-callbacks */
import { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Center, Spinner } from 'src/components/atoms';
import { PaginatedTableSearch } from 'src/components/atomsLegacy';
import DataIntegrationInfo from 'src/components/atomsLegacy/DataIntegrationInfo';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  EmptyState,
  Modal,
  UserRoleAuditLogModal,
} from 'src/components/molecules';
import { SelectDropdownGeneric } from 'src/components/moleculesLegacy';
import { PaginatedTable } from 'src/components/organisms';
import { EditUserModal } from 'src/components/organismsLegacy';
import * as Integration from 'src/data-integration/types';
import { useCalculateTableRows, usePageQueryParams } from 'src/hooks';
import { State, User } from 'src/types';
import * as Types from '../../../types';
import { AppStatus } from '../../../types';
import { organisationRolesUpdated } from './User.slice';
import {
  ButtonsRow,
  EmptyStateContainer,
  PageContainer,
  TableActions,
  TableContainer,
  TableHeader,
} from './Users.styles';
import {
  checkAllStatusesAreEqual,
  useExpireUsersMutation,
  useInviteMultipleUsersMutation,
  useInviteSingleUserMutation,
  useRemoveMultipleUsersMutation,
  useRemoveUserMutation,
} from './helpers';
import { getInviteModalMessage } from './helpers/getInviteModalMessage';
import { useUsersTable } from './useUsersTable';

const PADDING = 24;
const ACTIONS_ROW = 40;
const MARGIN = 20;
const TABLE_HEADER = 56;
const ROW_HEIGHT = 56;
const MIN_ROWS = 7;
const PAGINATION_CONTAINER = 56;
const PAGINATION_PADDING = 6;
const PAGE_SPACE_EXCLUDING_ROWS =
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN + 2 * PAGINATION_PADDING;
const SCROLLBAR_HEIGHT = 16;
const MIN_TABLE_HEIGHT =
  MIN_ROWS * ROW_HEIGHT +
  TABLE_HEADER +
  SCROLLBAR_HEIGHT +
  PAGINATION_CONTAINER +
  2 * PAGINATION_PADDING;

const tableHeight = `calc(100vh - ${
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN - PAGINATION_CONTAINER
}px)`;

const FULL_WIDTH_THRESHOLD = 1900;

export const Users = () => {
  const dispatch = useDispatch();
  const { page, setPage } = usePageQueryParams('/users');
  const { rows } = useCalculateTableRows(
    ROW_HEIGHT,
    MIN_ROWS,
    PAGE_SPACE_EXCLUDING_ROWS,
  );
  const [numPages, setNumPages] = useState(1);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);

  const hasDataIntegration = useSelector(
    (state: State) =>
      state.integrationStatus.dataIntegration !== Integration.Types.NONE,
  );
  const hasActiveScimProvider = useSelector(
    (state: State) =>
      state.organisation.organisation?.activeScimProvider === true,
  );
  const expiryPeriod = useSelector(
    (state: State) => state.organisation.organisation?.expiryPeriod,
  );

  const features = useSelector((state: State) => state.features);

  const {
    columns,
    organisationRoles,
    visibleColumns,
    updateVisibleColumns,
    searchUsers,
    sortableColumns,
    usersCount,
    sortColumns,
    tableData,
    sortedColumn,
    isLoading,
    isError,
    allSelected,
    allStatuses,
    selectedUsers,
    setSelectedUsers,
    selectUser,
    selectAllUsers,
    searchValue,
    userSearchOptions,
  } = useUsersTable(rows, page);

  // Reset page number on error
  useEffect(() => {
    if (isError && page !== 1) {
      setPage(1);
    }
  }, [setPage, isError, page]);

  // Update number of pages when table data or rows change
  useEffect(() => {
    if (usersCount) {
      setNumPages(Math.ceil(usersCount / rows));
    }
  }, [usersCount, rows]);

  // Set page to highest page number if greater than max
  useEffect(() => {
    if (Number(page) > numPages) {
      setPage(numPages);
    }
  }, [numPages, page, setPage]);

  useEffect(() => {
    dispatch(organisationRolesUpdated(organisationRoles ?? []));
  }, [dispatch, organisationRoles]);

  const [modalState, setModalState] = useState<{
    title: string;
    description: string | ReactNode;
    buttonText: string;
    onConfirm: () => void;
  }>({
    title: '',
    description: '',
    buttonText: '',
    onConfirm: () => {},
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(
    window.innerWidth > FULL_WIDTH_THRESHOLD,
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const currentWidth = window.innerWidth;
      setIsFullWidth(currentWidth > FULL_WIDTH_THRESHOLD);
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const expireUsers = useExpireUsersMutation();
  const inviteUsers = useInviteMultipleUsersMutation();
  const removeUsers = useRemoveMultipleUsersMutation();

  const inviteSingleUser = useInviteSingleUserMutation(() =>
    setShowConfirmationModal(false),
  );
  const removeSingleUser = useRemoveUserMutation();

  const isUsersStatusInvited = () => {
    return allStatuses[0] === AppStatus.INVITED;
  };

  const isUsersStatusUninvited = () => {
    return allStatuses[0] == AppStatus.NOT_INVITED;
  };

  const isUsersStatusExpiring = () => {
    return allStatuses[0] === AppStatus.EXPIRING;
  };

  const showInvitedUsersDeactivateButton = () => {
    return (
      checkAllStatusesAreEqual(allStatuses) &&
      allStatuses.length > 0 &&
      isUsersStatusInvited()
    );
  };

  const showDeactivateButtonForExpiringOrUninvitedUsers = () => {
    return (
      checkAllStatusesAreEqual(allStatuses) &&
      allStatuses.length > 0 &&
      (isUsersStatusExpiring() || isUsersStatusUninvited())
    );
  };

  const isAppReportingEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.APP_REPORTING,
  );

  const showSendInviteButton = () => {
    if (!isAppReportingEnabled) {
      return false;
    }
    return (
      selectedUsers.length > 0 &&
      !selectedUsers.some((user) => user.source === Types.UserSource.GUEST_USER)
    );
  };

  const thisUserDescription =
    selectedUsers.length === 1 ? 'this user' : 'these users';
  const deleteAllDraftsDescription = () => {
    let canCreateReports = false;
    canCreateReports = Boolean(
      organisationRoles?.some((orgRole) =>
        selectedUsers.some((user) =>
          user.SystemRoles?.some((role) =>
            role.name === orgRole.name
              ? orgRole.publicSystemPermissions?.canCreateReport
              : false,
          ),
        ),
      ),
    );

    return canCreateReports
      ? `Reshub users access will be removed and their draft reports will be deleted immediately when deactivated.`
      : ``;
  };
  const deactivateTitle = () => {
    return selectedUsers.length === 1
      ? 'Deactivate User?'
      : 'Deactivate Users?';
  };
  const deactivateDescription = () => {
    return `By clicking 'Deactivate' below you will start the ${expiryPeriod} days deactivation period, 
    after which ${thisUserDescription} will lose access to the Vault app and any of their reports. 
    ${deleteAllDraftsDescription()}`;
  };

  const deactivateNowDescription = () => {
    return `Are you sure you want to deactivate ${thisUserDescription} now? ${deleteAllDraftsDescription()}`;
  };

  return (
    <>
      {hasDataIntegration && <DataIntegrationInfo fullScreen />}
      <PageContainer className="routing-body-content" data-testid="users-page">
        <TableHeader>
          <TableActions>
            <PaginatedTableSearch
              options={userSearchOptions}
              applyFilter={searchUsers}
            />
            <SelectDropdownGeneric
              options={columns}
              withBorder={false}
              isMulti={true}
              defaultValue={visibleColumns.map((visibleCol) => ({
                label: visibleCol.label,
                value: visibleCol?.value ?? '',
              }))}
              label={'Show/Hide columns'}
              zIndex={12}
              customStyle={{
                controlStyle: {
                  backgroundColor: 'transparent',
                  width: '172px',
                },
                valueContainer: {
                  paddingLeft: '0',
                  paddingRight: '0',
                  fontSize: '15px',
                },
                dropdownIndicator: {
                  paddingLeft: '0',
                  paddingRight: '0',
                  marginRight: '8px',
                },
              }}
              onChange={updateVisibleColumns}
              dataQa="show-hide-columns-button"
              classNamePrefix="select"
            />
          </TableActions>
          <ButtonsRow>
            {showInvitedUsersDeactivateButton() && (
              <Button
                variant="secondary"
                size="medium"
                data-testid={'deactivate-users-button'}
                onClick={() => {
                  setModalState({
                    title: deactivateTitle(),
                    description: deactivateDescription(),
                    buttonText: 'Deactivate',
                    onConfirm: async () => {
                      expireUsers(selectedUsers.map((u) => String(u.id)));
                      setShowConfirmationModal(false);
                      setSelectedUsers([]);
                    },
                  });
                  setShowConfirmationModal(true);
                }}
                width="150px"
              >
                <>
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className={'uil uil-trash-alt'}
                  />
                  Deactivate
                </>
              </Button>
            )}
            {showDeactivateButtonForExpiringOrUninvitedUsers() && (
              <Button
                variant="secondary"
                size="medium"
                data-testid={'deactivate-users-now-button'}
                onClick={() => {
                  setModalState({
                    title: deactivateTitle(),
                    description: deactivateNowDescription(),
                    buttonText: 'Yes',
                    onConfirm: () => {
                      removeUsers(selectedUsers.map((u) => String(u.id)));
                      setShowConfirmationModal(false);
                      setSelectedUsers([]);
                    },
                  });
                  setShowConfirmationModal(true);
                }}
                width="150px"
              >
                <>
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className={'uil uil-trash-alt'}
                  />
                  Deactivate
                </>
              </Button>
            )}
            {showSendInviteButton() && (
              <Button
                variant="secondary"
                size="medium"
                data-testid={'send-users-email-button'}
                onClick={() => {
                  const message = getInviteModalMessage(selectedUsers.length);
                  setModalState({
                    title:
                      selectedUsers.length === 1
                        ? 'Invite user?'
                        : 'Invite users?',
                    description: message,
                    buttonText: 'Send',
                    onConfirm: () => {
                      inviteUsers(selectedUsers.map((u) => String(u.id)));
                      setShowConfirmationModal(false);
                      setSelectedUsers([]);
                    },
                  });
                  setShowConfirmationModal(true);
                }}
                width="185px"
                noTextWrap
              >
                <>
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className={'uil uil-envelope'}
                  />
                  Send reporting invite
                </>
              </Button>
            )}
            {selectedUsers.length === 1 && (
              <Button
                variant="secondary"
                size="medium"
                data-testid="edit-user-button"
                onClick={() => setShowEditUserModal(true)}
                width="150px"
              >
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className={'uil uil-pen'}
                />
                Edit user
              </Button>
            )}
            <Button
              variant="secondary"
              size="medium"
              data-testid="audit-log-button"
              onClick={() => setShowAuditLogModal(true)}
              width="150px"
            >
              <StyledIconInButton
                size="medium"
                position="left"
                className={'uil uil-file-alt'}
              />
              Audit log
            </Button>
            {(hasActiveScimProvider || !hasDataIntegration) && (
              <Button
                variant="secondary"
                size="medium"
                data-testid="add-user-button"
                onClick={() => {
                  setSelectedUsers([]);
                  setShowEditUserModal(true);
                }}
                width="150px"
              >
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className={'uil uil-user-plus'}
                />
                Add user
              </Button>
            )}
          </ButtonsRow>
        </TableHeader>
        {isLoading ? (
          <Center>
            <Spinner
              variant="secondary"
              size="large"
              data-testid="users-table-loading"
            />
          </Center>
        ) : (
          <TableContainer>
            <PaginatedTable
              isFullWidth={isFullWidth}
              sortableColumns={sortableColumns}
              height={tableHeight}
              tableData={tableData}
              minHeight={MIN_TABLE_HEIGHT}
              columns={columns}
              onColumnSort={sortColumns}
              data-testid="users-table"
              maxRows={rows < MIN_ROWS ? MIN_ROWS : rows}
              rowHeight={ROW_HEIGHT}
              isScrollable={true}
              fixedRowHeight={true}
              sortedColumn={sortedColumn}
              numPages={numPages}
              showPagination
              isAlreadyPaginated
              handleClickPage={(index: number) => setPage(index)}
              onRowClick={selectUser}
              borderRadius="12px"
              paginationPadding="18px 0px"
              visibleColumns={visibleColumns}
              currentPage={page}
              customEmptyState={
                <EmptyStateContainer>
                  <EmptyState
                    data-testid="users-table-empty-state"
                    size="default"
                    title={
                      searchValue
                        ? 'No users found.'
                        : `You don't have any users.`
                    }
                    description={
                      searchValue
                        ? 'Review your filters or refine your search to view more results.'
                        : 'You have currently no users in the system.'
                    }
                  />
                </EmptyStateContainer>
              }
              emptyText={
                searchValue ? 'No users found.' : `You don't have any users.`
              }
              allowRowSelect
              selectedRows={selectedUsers.map((user) => `${user.id}`)}
              selectAll={selectAllUsers}
              allSelected={allSelected}
            />
          </TableContainer>
        )}
        <EditUserModal
          show={showEditUserModal}
          openDeactivateDialog={() => {
            setModalState({
              title: deactivateTitle(),
              description: deactivateDescription(),
              buttonText: 'Deactivate',
              onConfirm: () => {
                expireUsers([String(selectedUsers[0].id)]);
                setShowEditUserModal(false);
                setShowConfirmationModal(false);
                setSelectedUsers([]);
              },
            });
            setShowConfirmationModal(true);
          }}
          openDeactivateNowDialog={() => {
            setModalState({
              title: deactivateTitle(),
              description: deactivateNowDescription(),
              buttonText: 'Yes',
              onConfirm: () => {
                removeSingleUser(String(selectedUsers[0].id));
                setShowEditUserModal(false);
                setShowConfirmationModal(false);
                setSelectedUsers([]);
              },
            });
            setShowConfirmationModal(true);
          }}
          handleClose={() => setShowEditUserModal(false)}
          selectedUser={
            selectedUsers.length ? (selectedUsers[0] as User) : null
          }
          inviteSingleUser={inviteSingleUser}
          onSuccess={() => {
            setShowEditUserModal(false);
            setSelectedUsers([]);
          }}
        />
        <Modal
          size="medium"
          title={modalState.title}
          description={modalState.description}
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          onConfirm={modalState.onConfirm}
          onCancel={() => setShowConfirmationModal(false)}
          confirmButtonText={modalState.buttonText}
          data-testid="users-table-modal"
        />
        <UserRoleAuditLogModal
          isOpen={showAuditLogModal}
          setIsOpen={setShowAuditLogModal}
        />
      </PageContainer>
    </>
  );
};
